import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const InviteArtistsDialog = ({ open, onClose, crewId, crewName, inviteUrl, onInviteSent, refMode, onRefModeChange, targetType = "crew", clubId }) => {
  // Phase 19.0 — Generalized to support both Crews/Groups and Clubs. When
  // `targetType="club"` we send invites to `/clubs/{clubId}/invite-user` and
  // hide the referral/invite-link controls (clubs don't carry invite codes —
  // joining is a paid Stripe action, so the share surface is just the page URL).
  const isClubMode = targetType === "club";
  const targetId = isClubMode ? clubId : crewId;
  const inviteEndpoint = isClubMode
    ? `/clubs/${targetId}/invite-user`
    : `/crews/${targetId}/invite-user`;
  const emailEndpoint = isClubMode ? null : `/crews/${targetId}/invite/email`;

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState({});
  const [copied, setCopied] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery(""); setResults([]); setCopied(false);
      setEmailValue(""); setEmailSending(false);
      // Phase 19.1.3 — reset per-row sending status so re-opening the dialog
      // doesn't show "✓ Invited" for users invited in a previous session.
      setSending({});
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/users/search', { params: { q: query } });
        if (!cancelled) setResults(r.data.users || []);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Search failed');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const handleInvite = async (userId, name) => {
    setSending((s) => ({ ...s, [userId]: true }));
    try {
      await api.post(inviteEndpoint, { invitee_id: userId });
      toast.success(`Invite sent to ${name}`);
      setSending((s) => ({ ...s, [userId]: 'sent' }));
      onInviteSent && onInviteSent(userId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
      setSending((s) => ({ ...s, [userId]: false }));
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const isValidEmail = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e || "");

  const handleSendEmailInvite = async () => {
    if (!emailEndpoint) {
      toast.error('Email invites are not available for clubs yet.');
      return;
    }
    const email = emailValue.trim().toLowerCase();
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setEmailSending(true);
    try {
      await api.post(emailEndpoint, { email });
      toast.success(`Email invite sent to ${email}`);
      setEmailValue("");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email invite');
    } finally {
      setEmailSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="invite-artists-dialog"
    >
      <div
        className="w-full max-w-lg bg-[#161620] border border-purple-500/40 rounded-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Invite artists to {crewName}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl" aria-label="Close">×</button>
        </div>

        {/* Phase 15.5 P5 — Referral attribution on the invite link */}
        {!isClubMode && inviteUrl && onRefModeChange && (
          <div className="mb-3 p-3 rounded-lg bg-black/40 border border-gray-800" data-testid="invite-ref-attribution">
            <p className="text-xs text-gray-400 mb-2">Referral credit when people join via this link</p>
            <div className="flex gap-2 flex-wrap text-xs">
              {[
                { key: 'personal', label: '🪪 My referral', desc: 'You earn referral leaderboard credit' },
                { key: 'crew', label: '👥 Crew credit', desc: 'Crew gets the join, no personal credit' },
                { key: 'none', label: '➖ None', desc: 'Plain invite link' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onRefModeChange(opt.key)}
                  data-testid={`invite-ref-${opt.key}`}
                  className={`px-3 py-1.5 rounded-full border transition-colors ${
                    (refMode || 'personal') === opt.key
                      ? 'bg-purple-600/40 border-purple-400 text-white'
                      : 'bg-black/30 border-gray-700 text-gray-300 hover:border-purple-500/60'
                  }`}
                  title={opt.desc}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Shareable link */}
        {!isClubMode && inviteUrl && (
          <div className="mb-4 p-3 rounded-lg bg-black/40 border border-gray-800">
            <p className="text-xs text-gray-400 mb-2">Shareable invite link</p>
            <div className="flex gap-2">
              <Input
                readOnly
                value={inviteUrl}
                className="bg-black/40 text-white border-gray-700 text-xs"
                data-testid="invite-link-input"
              />
              <Button
                onClick={handleCopyLink}
                className="bg-purple-600 hover:bg-purple-500 text-white"
                data-testid="copy-invite-link"
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>
        )}

        {/* Email invite */}
        {!isClubMode && (
        <div className="mb-5 p-3 rounded-lg bg-black/40 border border-gray-800">
          <p className="text-xs text-gray-400 mb-2">Invite by email</p>
          <div className="flex gap-2">
            <Input
              type="email"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              placeholder="artist@example.com"
              className="bg-black/40 text-white border-gray-700 text-sm"
              data-testid="invite-email-input"
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendEmailInvite(); }}
            />
            <Button
              onClick={handleSendEmailInvite}
              disabled={emailSending || !emailValue.trim()}
              className="bg-pink-600 hover:bg-pink-500 text-white"
              data-testid="invite-email-send"
            >
              {emailSending ? 'Sending…' : 'Send'}
            </Button>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            We'll email them a direct join link for {crewName}.
          </p>
        </div>
        )}

        {/* Search */}
        <div className="mb-3">
          <p className="text-xs text-gray-400 mb-2">Invite existing artists</p>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search artists by name or email…"
            className="bg-black/40 text-white border-gray-700"
            data-testid="invite-search-input"
          />
        </div>

        {searching && <p className="text-sm text-gray-400 text-center py-4">Searching…</p>}

        {!searching && query.length >= 2 && results.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">No artists match "{query}"</p>
        )}

        <div className="space-y-2">
          {results.map((u) => {
            const status = sending[u.user_id];
            return (
              <div
                key={u.user_id}
                className="flex items-center gap-3 p-2 rounded-lg bg-gray-900/40 border border-gray-800"
                data-testid={`invite-search-result-${u.user_id}`}
              >
                <img
                  src={getProfilePhotoUrl(u)}
                  alt={u.artist_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{u.artist_name || u.name}</p>
                  {u.handle && (
                    <p className="text-[11px] text-purple-300/70 truncate font-mono">@{u.handle}</p>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => handleInvite(u.user_id, u.artist_name)}
                  disabled={status === true || status === 'sent'}
                  className={status === 'sent'
                    ? 'bg-green-600 text-white'
                    : 'bg-purple-600 hover:bg-purple-500 text-white'}
                  data-testid={`invite-send-${u.user_id}`}
                >
                  {status === true ? '…' : status === 'sent' ? '✓ Invited' : 'Invite'}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InviteArtistsDialog;
